import { stripHtml } from "@/lib/text";

export type MediaItem = {
  url: string;
  filename?: string;
  img_path?: string;
  portrait_url?: string;
};

export type ThreadEventKind = "letter" | "postcard" | "emessage";
export type ThreadEventRole = "user" | "assistant";

export type ThreadEvent = {
  id: string;
  kind: ThreadEventKind;
  role: ThreadEventRole;
  sortTs: number;
  sortTsSecondary: number;
  text: string;
  photos: MediaItem[];
  postcardFrontUrl?: string;
  videos: MediaItem[];
  raw: any;
};

const parseDateToTs = (value: unknown): number => {
  if (typeof value !== "string" || !value) return 0;
  const ts = Date.parse(value);
  return Number.isFinite(ts) ? ts : 0;
};

export const getRawSortTs = (raw: any): number => {
  // Match legacy Angular semantics:
  // - primary: timestamp
  // - secondary: send_date (fallback: timestamp)
  // Note: Angular uses moment().unix() and assumes `timestamp` exists for sorting.
  const primary =
    parseDateToTs(raw && raw.timestamp) ||
    parseDateToTs(raw && raw.sent_at) ||
    parseDateToTs(raw && raw.created_at) ||
    parseDateToTs(raw && raw.updated_at);
  return primary;
};

export const getRawSortTsSecondary = (raw: any): number => {
  const primary = getRawSortTs(raw);
  const secondary = parseDateToTs(raw && raw.send_date);
  return secondary || primary;
};

const toText = (value: unknown): string => {
  if (typeof value !== "string") return "";
  return stripHtml(value).trim();
};

const getTextForRaw = (raw: any): string => {
  const isEmessage = raw && raw.message_type === "emessage";
  if (isEmessage) return toText(raw.text);
  return toText(raw.message) || toText(raw.body) || toText(raw.summary);
};

const normalizeMediaList = (list: unknown): MediaItem[] => {
  if (!Array.isArray(list)) return [];
  return list
    .map((p: any) => {
      const url = p && typeof p.url === "string" ? p.url : "";
      if (!url) return null;
      return {
        url,
        filename: typeof p.filename === "string" ? p.filename : undefined,
        img_path: typeof p.img_path === "string" ? p.img_path : undefined,
        portrait_url: typeof p.portrait_url === "string" ? p.portrait_url : undefined,
      } satisfies MediaItem;
    })
    .filter((p): p is MediaItem => !!p);
};

const getOutgoingPhotos = (raw: any): MediaItem[] => {
  const selected = normalizeMediaList(raw && raw.selectedPhotos);
  if (selected.length) return selected;
  return normalizeMediaList(raw && raw.photos);
};

const getPostcardFrontUrl = (raw: any): string | undefined => {
  const url =
    raw?.postcard?.frontImage?.url ??
    raw?.data?.postcard?.frontImage?.url ??
    raw?.data?.postcard?.frontImageUrl ??
    raw?.data?.postcard?.front_image_url;
  return typeof url === "string" && url ? url : undefined;
};

export const toThreadEvent = (raw: any): ThreadEvent | null => {
  const id = raw && raw.id != null ? String(raw.id) : "";
  if (!id) return null;

  const isEmessage = raw && raw.message_type === "emessage";
  const role: ThreadEventRole = isEmessage ? "assistant" : "user";

  const orderType = raw && typeof raw.order_type === "string" ? raw.order_type : "";
  const kind: ThreadEventKind = isEmessage
    ? "emessage"
    : orderType === "postcard"
      ? "postcard"
      : "letter";

  const sortTs = getRawSortTs(raw);
  const sortTsSecondary = getRawSortTsSecondary(raw);
  const text = getTextForRaw(raw);

  const photos =
    kind === "postcard"
      ? []
      : isEmessage
        ? normalizeMediaList(raw && raw.attachments)
        : getOutgoingPhotos(raw);
  const videos = isEmessage ? normalizeMediaList(raw && raw.videos) : [];

  const postcardFrontUrl = kind === "postcard" ? getPostcardFrontUrl(raw) : undefined;

  return {
    id,
    kind,
    role,
    sortTs,
    sortTsSecondary,
    text,
    photos,
    postcardFrontUrl,
    videos,
    raw,
  };
};

export const normalizeThreadEvents = (items: readonly any[]): ThreadEvent[] => {
  const byId = new Map<string, ThreadEvent>();
  for (const raw of items) {
    const evt = toThreadEvent(raw);
    if (!evt) continue;
    byId.set(evt.id, evt);
  }
  return Array.from(byId.values()).sort((a, b) => {
    if (a.sortTs !== b.sortTs) return a.sortTs - b.sortTs;
    if (a.sortTsSecondary !== b.sortTsSecondary) return a.sortTsSecondary - b.sortTsSecondary;
    // Stable tie-breaker.
    return a.id.localeCompare(b.id);
  });
};
